import React, { Fragment, ReactElement, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Avatar from './Avatar/Avatar';
import XMarkIcon from './icons/XMark';

type ModalProps = {
  animation?: any;
  // Accepts a node OR a render function. The type used to promise only a
  // function, but the JSX below dropped it straight in as a child and React
  // ignores function children — so every caller passing a function rendered an
  // EMPTY heading, silently. Most callers pass a plain string, which is why it
  // went unnoticed.
  title: React.ReactNode | (() => React.ReactNode);
  description?: string | ReactElement;
  cancelButtonText?: string;
  cancelButtonOnClick?: () => void;
  acceptButtonText: string;
  // When set, the accept action renders as an anchor rather than a button.
  // Keeps real link semantics, which is what lets the native shell hand the
  // URL to the system browser instead of navigating inside the WebView.
  acceptButtonHref?: string;
  acceptButtonOnClick: () => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Modal({
  animation,
  title,
  description,
  cancelButtonText,
  cancelButtonOnClick,
  acceptButtonText,
  acceptButtonHref,
  acceptButtonOnClick,
  open,
  setOpen,
}: ModalProps) {
  const acceptButtonRef = useRef(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={acceptButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-[opacity,transform] sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white p-2.5 -m-2.5 text-gray-400 transition-colors duration-150 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    {animation && (
                      <Avatar
                        playLoop={animation}
                        width={100}
                        height={100}
                        className="pointer-events-none mx-auto mb-3"
                      />
                    )}
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium text-gray-900 whitespace-pre-line"
                    >
                      {typeof title === 'function' ? title() : title}
                    </Dialog.Title>
                    {description ? (
                      <div className="mt-2">
                        {typeof description === 'string' ? (
                          <p className="text-sm text-gray-500">{description}</p>
                        ) : (
                          description
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 flex flex-col gap-3 sm:grid sm:grid-cols-2 sm:gap-3">
                  {acceptButtonHref ? (
                    <a
                      href={acceptButtonHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display w-full justify-center rounded-full border border-transparent bg-teal-300 px-4 py-3 text-base text-gray-800 shadow-sm transition-[background-color,transform] duration-150 hover:bg-teal-500 active:scale-[0.96] focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 inline-flex no-underline hover:no-underline"
                      onClick={acceptButtonOnClick}
                      ref={acceptButtonRef}
                    >
                      {acceptButtonText}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="font-display w-full justify-center rounded-full border border-transparent bg-teal-300 px-4 py-3 text-base text-gray-800 shadow-sm transition-[background-color,transform] duration-150 hover:bg-teal-500 active:scale-[0.96] focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
                      onClick={acceptButtonOnClick}
                      ref={acceptButtonRef}
                    >
                      {acceptButtonText}
                    </button>
                  )}
                  {/* An informational dialog has nothing to cancel. Render the button only
                      when a caller supplies a label, instead of an empty pill. */}
                  {cancelButtonText && (
                    <button
                      type="button"
                      className="font-display w-full border-gray-300 justify-center rounded-full border bg-gray-50 px-4 py-3 text-base text-gray-800 shadow-sm transition-[background-color,transform] duration-150 hover:bg-gray-200 active:scale-[0.96] focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
                      onClick={cancelButtonOnClick}
                    >
                      {cancelButtonText}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
